import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/api";

const REASONS = [
  { value: "Billing & Subscription", icon: "💳", hint: "Invoices, plans, upgrades, refunds" },
  { value: "Technical Issue / Bug", icon: "🛠️", hint: "Something isn't working right" },
  { value: "Account & Login", icon: "🔐", hint: "Access, passwords, seats" },
  { value: "Feature Request", icon: "💡", hint: "An idea to make SalesArena better" },
  { value: "Data & Privacy", icon: "🛡️", hint: "Your data, exports, deletion" },
  { value: "Other", icon: "💬", hint: "Anything else — we're here" },
];

export default function PremiumSupport() {
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { document.title = "Premium Support — SalesArena HQ"; }, []);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please attach an image file.");
    if (f.size > 10 * 1024 * 1024) return toast.error("Image is too large (max 10MB).");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  const clearFile = () => { setFile(null); setPreview(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!reason) return toast.error("Please pick a reason.");
    if (description.trim().length < 10) return toast.error("Please add a few more details (10+ characters).");
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("reason", reason);
      fd.append("description", description.trim());
      if (file) fd.append("screenshot", file);
      await api.post("/support/contact", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
      toast.success("Message sent — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="support-sent">
        <div className="text-5xl mb-4">🏆</div>
        <h1 className="text-3xl font-black uppercase tracking-tight text-white">Message received.</h1>
        <p className="text-zinc-400 mt-3 leading-relaxed">
          Our team will reply to your email as soon as possible. For anything urgent you can also reach us directly at{" "}
          <a href="mailto:support@salesarenahq.com" className="text-cyan-400 font-semibold">support@salesarenahq.com</a>.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            data-testid="support-send-another"
            onClick={() => { setSent(false); setReason(""); setDescription(""); clearFile(); }}
            className="px-5 py-2.5 rounded-full border border-zinc-700 text-white font-semibold hover:bg-zinc-800 transition-colors"
          >
            Send another
          </button>
          <Link to="/dashboard/home" className="px-5 py-2.5 rounded-full bg-cyan-500 text-black font-bold hover:bg-cyan-400 transition-colors">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="support-page">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-cyan-400 mb-2">
          <span>★</span> Premium Support
        </div>
        <h1 className="text-4xl font-black uppercase tracking-tight text-white leading-none">How can we help?</h1>
        <p className="text-zinc-400 mt-3 leading-relaxed">
          Tell us what's going on and we'll get back to you by email. Real people, fast answers.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-7">
        <div>
          <label className="block text-sm font-bold uppercase tracking-wide text-zinc-300 mb-3">Reason</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {REASONS.map((r) => {
              const active = reason === r.value;
              return (
                <button
                  type="button"
                  key={r.value}
                  data-testid={`support-reason-${r.value.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                  onClick={() => setReason(r.value)}
                  className={`text-left p-4 rounded-2xl border transition-all ${
                    active
                      ? "border-cyan-500 bg-cyan-500/10 ring-1 ring-cyan-500"
                      : "border-zinc-800 bg-zinc-900/60 hover:border-zinc-600"
                  }`}
                >
                  <div className="text-2xl mb-1">{r.icon}</div>
                  <div className="text-white font-bold text-sm">{r.value}</div>
                  <div className="text-zinc-500 text-xs mt-0.5">{r.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label htmlFor="support-desc" className="block text-sm font-bold uppercase tracking-wide text-zinc-300 mb-3">
            Description
          </label>
          <textarea
            id="support-desc"
            data-testid="support-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={7}
            maxLength={5000}
            placeholder="Describe your issue or request in as much detail as you can — steps you took, what you expected, and what happened."
            className="w-full rounded-2xl bg-zinc-900/60 border border-zinc-800 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-white p-4 outline-none resize-y leading-relaxed"
          />
          <div className="text-right text-xs text-zinc-600 mt-1">{description.length}/5000</div>
        </div>

        <div>
          <label className="block text-sm font-bold uppercase tracking-wide text-zinc-300 mb-3">
            Screenshot <span className="text-zinc-600 normal-case font-medium">(optional — speeds up triage)</span>
          </label>
          {preview ? (
            <div className="relative inline-block" data-testid="support-screenshot-preview">
              <img src={preview} alt="Screenshot preview" className="max-h-48 rounded-xl border border-zinc-800" />
              <button
                type="button"
                data-testid="support-screenshot-remove"
                onClick={clearFile}
                className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-zinc-900 border border-zinc-700 text-white text-sm hover:bg-red-500 hover:border-red-500 transition-colors"
                aria-label="Remove screenshot"
              >
                ✕
              </button>
            </div>
          ) : (
            <label
              data-testid="support-screenshot-drop"
              className="flex items-center gap-3 p-4 rounded-2xl border border-dashed border-zinc-700 bg-zinc-900/40 hover:border-cyan-500 cursor-pointer transition-colors"
            >
              <div className="w-9 h-9 rounded-lg bg-zinc-800 flex items-center justify-center text-lg">📷</div>
              <div className="text-sm text-zinc-400">
                <span className="text-cyan-400 font-semibold">Attach a screenshot</span> — PNG, JPG, or HEIC, up to 10MB
              </div>
              <input type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="support-screenshot-input" />
            </label>
          )}
        </div>

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={sending}
            data-testid="support-submit"
            className="px-7 py-3 rounded-full bg-cyan-500 text-black font-black uppercase tracking-wide hover:bg-cyan-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : "Send message"}
          </button>
          <span className="text-zinc-500 text-sm">
            or email{" "}
            <a href="mailto:support@salesarenahq.com" className="text-cyan-400 font-semibold">support@salesarenahq.com</a>
          </span>
        </div>
      </form>
    </div>
  );
}
